import React, { useState } from 'react';
import {
    Modal,
    Box,
    TextField,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Button
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        width: '80%',
        maxHeight: '80%',
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflowY: 'auto',
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    searchInput: {
        marginLeft: theme.spacing(1),
        flex: 1,
    },
    templateItem: {
        backgroundColor: theme.palette.optionsBackground,
        color: theme.palette.primary,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
    },
    templateInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    category: {
        marginTop: theme.spacing(1),
    },
}));

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate }) => {
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [renderedContent, setRenderedContent] = useState('');

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const extractPlaceholders = (value) => {
        if (!value || typeof value !== "string") return [];
        const regex = /{{\s*([^}]+)\s*}}/g;
        const results = [];
        let match;
        while ((match = regex.exec(value)) !== null) {
            const token = String(match[1] || "").trim();
            if (token) results.push(token);
        }
        return [...new Set(results)];
    };

    const pushIfNotExists = (list, item) => {
        const exists = list.some((entry) => {
            return entry.prompt === item.prompt && entry.index === item.index && entry.buttonIndex === item.buttonIndex;
        });
        if (!exists) list.push(item);
    };

    const extractVariablesByComponent = (components) => {
        const variables = {
            header: [],
            body: [],
            footer: [],
            buttons: []
        };

        (components || []).forEach((component) => {
            const type = String(component?.type || "").toLowerCase();
            const text = component?.text || "";
            if (!type || !variables[type]) return;

            // Só pedir URL de imagem/vídeo no header se houver placeholder {{...}} no texto do header
            // (mídia fixa na Meta não exige preencher aqui — o backend usa example/header_handle).

            // Variáveis em texto do componente (BODY/HEADER/FOOTER)
            const placeholders = extractPlaceholders(text);
            placeholders.forEach((token, idx) => {
                pushIfNotExists(variables[type], {
                    type: "text",
                    token, // nome do placeholder {{var1}}, {{1}}, etc.
                    prompt: `${type.toUpperCase()} {{${token}}}`,
                    index: idx
                });
            });

            // Variáveis em botões (URL/COPY_CODE etc)
            if (type === "buttons" && component?.buttons) {
                let buttons = [];
                try {
                    buttons = typeof component.buttons === "string"
                        ? JSON.parse(component.buttons)
                        : component.buttons;
                } catch (_e) {
                    buttons = [];
                }

                (buttons || []).forEach((button, buttonIndex) => {
                    const sourceText = String(button?.url || button?.text || button?.example || "");
                    const buttonVars = extractPlaceholders(sourceText);
                    if (buttonVars.length === 0) {
                        return;
                    }
                    buttonVars.forEach((token, idx) => {
                        pushIfNotExists(variables.buttons, {
                            type: button?.type || "text",
                            token,
                            prompt: `BUTTON ${buttonIndex + 1} {{${token}}}`,
                            index: idx,
                            buttonIndex
                        });
                    });
                });
            }
        });

        return variables;
    };



    const handleSelectTemplate = (template) => {
        const components = template?.components || [];
        const extractedVariables = extractVariablesByComponent(components);

        setSelectedTemplate(template);
        setVariables(extractedVariables);
        setRenderedContent(components.map((component) => component?.text).join(`\n`));
    };

    // const generateBodyToSave = (content, variables) => {
    //     let bodyToSave = content;
    //     console.log("variables", variables)
    //     console.log("bodyToSave", bodyToSave)
    //     Object.keys(variables).forEach((componentType) => {
    //         console.log("componentType", componentType)
    //         const componentVariables = variables[componentType];
    //         console.log("componentVariables", componentVariables, typeof componentVariables)
    //         // Verificar se componentVariables é um array
    //         if (Array.isArray(componentVariables)) {
    //             componentVariables.forEach((variable, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = variable?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else if (typeof componentVariables === 'object') {
    //             Object.keys(componentVariables).forEach((key, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = componentVariables[key]?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else {
    //             console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
    //         }
    //     });
    //     console.log("bodyToSave", bodyToSave)

    //     return bodyToSave;
    // };
    /** Retorna o nome do placeholder (ex.: var1, 1) a partir do descritor do campo. */
    const getPlaceholderTokenFromDescriptor = (descriptor) => {
        if (!descriptor) return null;
        if (descriptor.token) return String(descriptor.token).trim();
        if (descriptor.prompt) {
            const m = /\{\{([^}]+)\}\}/.exec(descriptor.prompt);
            return m ? String(m[1]).trim() : null;
        }
        return null;
    };

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    /**
     * Monta o texto pré-visualizado / salvo no ticket.
     * variableValues: { body: { 0: { value }, 1: { value } }, ... }
     * variableDescriptors: mesmo shape do extractVariablesByComponent (para saber se é {{var1}} ou {{1}}).
     */
    const generateBodyToSave = (content, variableValues, variableDescriptors) => {
        let bodyToSave = content;

        Object.keys(variableValues || {}).forEach((componentType) => {
            const componentVariables = variableValues[componentType];
            const descriptors = (variableDescriptors && variableDescriptors[componentType]) || [];

            if (!componentVariables || typeof componentVariables !== "object") return;

            const keys = Object.keys(componentVariables).sort((a, b) => Number(a) - Number(b));

            if (componentType === "header") {
                keys.forEach((key) => {
                    const idx = parseInt(key, 10);
                    const value = String(componentVariables[key]?.value || "").trim();
                    const descriptor = descriptors[idx];
                    const token = getPlaceholderTokenFromDescriptor(descriptor);
                    if (value.startsWith("http")) {
                        bodyToSave = `${value} \n ${bodyToSave}`;
                    } else if (token) {
                        bodyToSave = bodyToSave.replace(
                            new RegExp(`\\{\\{\\s*${escapeRegExp(token)}\\s*\\}\\}`, "g"),
                            value
                        );
                    } else {
                        bodyToSave = bodyToSave.replace(`{{${idx + 1}}}`, value);
                    }
                });
                return;
            }

            keys.forEach((key) => {
                const idx = parseInt(key, 10);
                const value = componentVariables[key]?.value ?? "";
                const descriptor = descriptors[idx];
                const token = getPlaceholderTokenFromDescriptor(descriptor);
                if (token) {
                    bodyToSave = bodyToSave.replace(
                        new RegExp(`\\{\\{\\s*${escapeRegExp(token)}\\s*\\}\\}`, "g"),
                        String(value)
                    );
                } else {
                    bodyToSave = bodyToSave.replace(`{{${idx + 1}}}`, String(value));
                }
            });
        });

        return bodyToSave;
    };

    const hasRequiredVariables = () => {
        return Object.keys(variables).some((componentType) => {
            return (variables[componentType] || []).length > 0;
        });
    };

    const hasEmptyRequiredValues = () => {
        return Object.keys(variables).some((componentType) => {
            const requiredVars = variables[componentType] || [];
            return requiredVars.some((_, index) => {
                const value = variableValues?.[componentType]?.[index]?.value;
                return !String(value || "").trim();
            });
        });
    };

    const handleSendTemplate = () => {
        if (hasRequiredVariables() && hasEmptyRequiredValues()) {
            return;
        }
        const bodyToSave = generateBodyToSave(renderedContent, variableValues, variables);
        const templateWithVariables = {
            ...selectedTemplate,
            variables: variableValues,
            bodyToSave
        };
        onSelectTemplate(templateWithVariables);
    };

    const handleVariableChange = (componentType, index, value, buttonIndex) => {
        // Atualiza o estado das variáveis por tipo de componente
        const newComponentValues = { ...variableValues[componentType], [index]: { value, buttonIndex } };
        const newValues = { ...variableValues, [componentType]: newComponentValues };
        setVariableValues(newValues);
    };

    const filteredTemplates = templates.filter((template) => {
        const statusOk =
            String(template?.status || "APPROVED").toUpperCase() === "APPROVED";
        const searchOk = template?.shortcode
            ?.toLowerCase()
            .includes(search?.toLowerCase());
        return statusOk && searchOk;
    });

    return (
        <Modal
            open={open}
            onClose={handleClose}
            className={classes.modal}
            maxWidth="md"
            aria-labelledby="template-modal-title"
        >
            <Box className={classes.modalContent}>
                <Typography variant="h6" id="template-modal-title">
                    Templates do WhatsApp
                </Typography>
                {!selectedTemplate && (
                    <>
                        <div className={classes.searchContainer}>
                            <SearchIcon />
                            <TextField
                                variant="outlined"
                                placeholder="Pesquisar Templates"
                                value={search}
                                onChange={handleSearchChange}
                                className={classes.searchInput}
                            />
                        </div>

                        <List>
                            {filteredTemplates.map((template, index) => (
                                <ListItem
                                    key={index}
                                    className={classes.templateItem}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    <ListItemText
                                        primary={
                                            <div className={classes.templateInfo}>
                                                <Typography variant="body1">{template.shortcode}</Typography>
                                                <Typography variant="body2">Idioma: {template.language}</Typography>
                                            </div>
                                        }
                                        secondary={
                                            <>
                                                <Typography variant="body2" component="div">
                                                    Conteúdo do Template: {template?.components?.map(component => component?.text).join(`\n`)}
                                                </Typography>
                                                <Typography variant="body2" className={classes.category}>
                                                    Categoria: {template.category}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
                {selectedTemplate && (
                    <Box mt={2}>
                        <Typography variant="h6">Detalhes do Template Selecionado</Typography>
                        <Typography variant="body1">Nome: {selectedTemplate.name}</Typography>
                        <Typography variant="body2">Conteúdo: {renderedContent}</Typography>
                        <Typography variant="body2">Idioma: {selectedTemplate.language}</Typography>
                        <Typography variant="body2">Categoria: {selectedTemplate.category}</Typography>
                        {/* Renderização dos campos para preencher as variáveis */}
                        {Object.keys(variables).map((componentType, index) => (
                            <div key={`${componentType}-${index}`}>
                                {variables[componentType].length > 0 && (
                                    <>
                                        <Typography variant="h6">{componentType.toUpperCase()}</Typography>
                                        {variables[componentType].map((variable, index) => (
                                            <TextField
                                                key={`${componentType}-${index}`}
                                                label={`${variable?.prompt}`}
                                                value={variableValues[componentType]?.[index]?.value || ''}
                                                onChange={(e) => handleVariableChange(componentType, index, e.target.value, variable?.index || 0)}
                                                required
                                                error={!String(variableValues[componentType]?.[index]?.value || "").trim()}
                                                fullWidth
                                                margin="normal"
                                            />
                                        ))}
                                    </>
                                )}
                            </div>
                        ))}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendTemplate}
                            disabled={hasRequiredVariables() && hasEmptyRequiredValues()}
                            fullWidth
                            style={{ marginTop: 16 }}
                        >
                            Enviar
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSelectedTemplate(null);
                                setVariableValues({});
                                setRenderedContent('');
                            }}
                            fullWidth
                            style={{ marginTop: 8 }}
                        >
                            Voltar
                        </Button>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default TemplateModal;
